// Which product a BLE device is — learned from what it SAYS, not from the backend.
//
// WHY THIS EXISTS. Every control screen in this app is chosen by DeviceID
// (core/devices/deviceMeta.deviceTypeKey). On the bench there is no DeviceID to
// read: the device is not in an account yet, so the backend has never heard of
// it. Without an answer to "what is this thing" there is nothing to draw, and
// the bench falls back to typing relay numbers into a probe button.
//
// ── Two sources, and the SECOND is the reliable one ────────────────────────
//
//   INFO / WHOAMI   may name the type outright. The reply format is NOT frozen
//                   in this repo — the spec lives in the firmware project and is
//                   still being negotiated — so the parser here is deliberately
//                   tolerant of shape and strict about the VALUE: any
//                   `<key>=<n>` / `<key>:<n>` whose key reads like a device
//                   type, and only if `n` is a DeviceID this app knows.
//
//   STATUS          `[083A8D406BE4]1,0,1,0#BLE`. This format IS frozen (see
//                   core/devices/statusFormat) and the number of values is the
//                   channel count. Four channels is a 4-touch whatever INFO
//                   chooses to call it.
//
// So a type inferred from the channel count is always available, and a type the
// device states outright wins over it. Neither is trusted blindly: the bench
// keeps a manual override, because the wrong panel drawn confidently is worse
// than no panel at all — it makes button 3 turn on the wrong load and reads as
// broken firmware.

import { channelsFor, parseStatus } from '../devices/statusFormat';
import { deviceTypeKey, relayCount } from '../devices/deviceMeta';

// A channel count on its own cannot name a product, only a shape. Two counts
// are genuinely ambiguous and the choice here is the one that fails softest:
//
//   1 channel   55 (1-gang switch) over 53 (fan). A fan drawn as a switch still
//               toggles; a switch drawn as a fan dial sends speeds it ignores.
//   5 channels  92 (4+1) over 1 — 92 is what ships.
//
// Both are overridable on the bench, which is the point of the override.
export const DEVICE_ID_BY_CHANNELS = {
  1: 55,
  2: 56,
  3: 91,
  4: 49,
  5: 92,
  8: 93,
  9: 87,
};

// How much to trust each source, so a weaker one cannot overwrite a stronger.
const TRUST = { info: 3, stated: 2, channels: 1 };

/** Ranking for `via` — higher wins. */
export const identityRank = (via) => TRUST[via] ?? 0;

/** Whether this app has a control layout for a DeviceID. */
export function isKnownDeviceId(id) {
  const n = Number(id);
  if (!Number.isFinite(n)) return false;
  return channelsFor(n) != null || deviceTypeKey(n) !== 'unsupported';
}

/** How many channels a DeviceID carries — the status width, not just relays. */
export function channelCountFor(id) {
  const n = Number(id);
  return channelsFor(n) ?? (relayCount(n) || null);
}

/** The DeviceID a status of `n` values most likely came from. */
export function deviceIdForChannels(n) {
  return DEVICE_ID_BY_CHANNELS[Number(n)] ?? null;
}

// `<key><sep><digits>`, where the key reads like a device type and the separator
// is any of `= : space`. Covers `type=49`, `DeviceType: 49`, `deviceid=49`,
// `INFO dtype 49` and the JSON-ish `"model":49` in one pass.
const TYPE_KEY_VALUE =
  /\b(?:device[_\s-]?id|device[_\s-]?type|dev[_\s-]?type|d?type|model)"?\s*[:=\s]\s*"?(\d{1,3})\b/i;

// ⚠️ THERE IS NO `WHOAMI:<uno>,<type>` REPLY. This file used to carry a pattern
// for one. The firmware answers WHOAMI with OWNERSHIP, not identity:
//
//     ASSIGNED:<owner_id>,<mesh_id>,<node_index>,<location_id>,<room_id>
//     UNASSIGNED
//
// — no DeviceType anywhere in it. The pattern matched nothing on real hardware
// and, worse, implied a second source of truth that does not exist. INFO is the
// only command that reports the type.

// ── THE REAL `INFO` REPLY ──────────────────────────────────────────────────
//
//     E072A14582D0,49,4,1
//     └ UniqueNumber  └ DeviceType  └ channels  └ firmware version
//
// Confirmed against hardware, and it is BARE CSV — no keys, no prefix. The
// tolerant key=value parser above matches none of it, and parseStatus rejects
// it too (no brackets), so before this existed an INFO reply taught nothing and
// the panel came up only after a STATUS frame.
//
// It is the BEST source there is, and better than the type table: the device
// reports its own CHANNEL COUNT, so a product this app has no entry for still
// draws the right number of buttons.
//
// ⚠️ THE LAST FIELD IS A STRING, NOT A NUMBER. The firmware builds this with
//
//     reply("%s,%d,%d,%s", UniqueNumber, device_type_effective(),
//           tuya_mcu_channel_count(), app->version);
//
// and `app->version` is `esp_app_desc_t.version` — a semver-ish build string
// like "1.4.2", or "?" when the descriptor is missing. Requiring digits there
// made the whole pattern fail on any real firmware, which put this straight
// back to learning nothing from INFO. Anything is accepted, and it is kept as
// text: it is a label, never arithmetic.
//
// ⚠️ Anchored at the start on a unique-number-shaped field. Without the anchor
// this would also match the tail of anything else comma-separated.
const INFO_REPLY =
  /^\s*\[?([0-9A-F]{6,16})\]?\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([^\r\n]*?))?\s*$/i;

// A panel nobody ships. Wider than any real product, narrow enough that a
// mis-parse cannot ask the UI for 900 buttons.
const MAX_CHANNELS = 16;

/**
 * The device's own `INFO` answer.
 *
 * -> { uniqueNumber, deviceId, channels, firmware } | null
 *
 * The DeviceType is passed through EVEN IF this app has no entry for it: the
 * channel count arrives in the same line, so the panel can still be drawn. An
 * unknown type degrades to unnamed switches, which is strictly better than
 * refusing to draw a device that just told us exactly what it is.
 */
export function parseInfoReply(text) {
  const m = INFO_REPLY.exec(String(text || ''));
  if (!m) return null;
  const channels = Number(m[3]);
  if (!Number.isFinite(channels) || channels < 1 || channels > MAX_CHANNELS) return null;
  const fw = (m[4] || '').trim();
  return {
    uniqueNumber: m[1].toUpperCase(),
    deviceId: Number(m[2]),
    channels,
    // '?' is the firmware's own "no app descriptor" marker — not a version.
    firmware: fw && fw !== '?' ? fw : null,
  };
}

/**
 * A DeviceID stated in a notification, or null.
 *
 * Only DeviceIDs this app can draw are returned — an unrecognised number is
 * treated as "not a device type", because in a tolerant parse it usually isn't.
 */
export function parseDeviceId(text) {
  const s = String(text || '');
  const m = TYPE_KEY_VALUE.exec(s);
  if (!m) return null;
  const id = Number(m[1]);
  return isKnownDeviceId(id) ? id : null;
}

/**
 * What one notification teaches about the device.
 *
 * -> { deviceId, channels, values, firmware, via } | null
 *
 *   via 'info'      the device's own INFO line — type AND channel count
 *   via 'stated'    the device named its type some other way
 *   via 'channels'  inferred from the width of a STATUS frame — a good guess
 *
 * In that order of trust, and the order matters: a later STATUS frame must not
 * quietly overwrite an answer the device gave outright.
 *
 * `values` is present only for a status frame, and is the raw numbers: a fan
 * channel's 40 means speed 40, so flattening it to a boolean here would throw
 * away the only thing it carries.
 */
export function learnFromNotification(text) {
  const info = parseInfoReply(text);
  if (info) {
    return {
      deviceId: info.deviceId,
      // The DEVICE's count, not the table's. If the two disagree the device is
      // right — it is the thing with the relays on it.
      channels: info.channels,
      values: null,
      firmware: info.firmware,
      via: 'info',
    };
  }

  const stated = parseDeviceId(text);
  const status = parseStatus(text);

  if (stated != null) {
    return {
      deviceId: stated,
      channels: channelCountFor(stated) ?? status?.values.length ?? null,
      values: status ? status.values : null,
      via: 'stated',
    };
  }

  if (status && status.values.length > 0) {
    const guess = deviceIdForChannels(status.values.length);
    if (guess == null) return null;
    return {
      deviceId: guess,
      channels: status.values.length,
      values: status.values,
      via: 'channels',
    };
  }

  return null;
}
